import {useCallback} from 'react';
import {toast} from 'sonner';
import {useTranslationWithKeys} from './useTranslationWithKeys';
import {
    extractErrorMessage,
    getLocalizedErrorMessage as getLocalizedErrorMessageUtil,
    isAuthError,
    isNetworkError,
    isServerError,
    isValidationError,
} from '../utils/errorHandler';

interface HandleApiErrorOptions {
    showToast?: boolean;
    fallbackMessage?: string;
    onError?: (error: any) => void;
}

export const useErrorHandler = () => {
    const {t} = useTranslationWithKeys();

    const getLocalizedErrorMessage = useCallback((error: any): string => {
        return getLocalizedErrorMessageUtil(error, t);
    }, [t]);

    const showErrorToast = useCallback((error: any) => {
        const message = getLocalizedErrorMessage(error);
        toast.error(message);
    }, [getLocalizedErrorMessage]);

    const showErrorInline = useCallback((error: any): string => {
        return getLocalizedErrorMessage(error);
    }, [getLocalizedErrorMessage]);

    const handleApiError = useCallback((error: any, options: HandleApiErrorOptions = {}) => {
        const {showToast = true, fallbackMessage, onError} = options;

        if (onError) {
            onError(error);
        }

        if (isAuthError(error)) {
            if (showToast) {
                toast.error(t('error.auth_required'));
            }
            return;
        }

        if (isNetworkError(error)) {
            if (showToast) {
                toast.error(t('error.network'));
            }
            return;
        }

        if (isServerError(error)) {
            if (showToast) {
                toast.error(t('error.server'));
            }
            return;
        }

        const specificMessage = getLocalizedErrorMessage(error);

        const isRawTranslationKey = (message: string) => {
            return message.startsWith('error.') && !message.includes(' ');
        };

        const message = isRawTranslationKey(specificMessage) && fallbackMessage
            ? fallbackMessage
            : specificMessage;

        if (showToast) {
            toast.error(message);
        }

        return message;
    }, [t, getLocalizedErrorMessage]);

    return {
        showErrorToast,
        showErrorInline,
        handleApiError,
        getLocalizedErrorMessage,
        isAuthError,
        isNetworkError,
        isServerError,
        isValidationError,
        extractErrorMessage,
    };
};
